<template>
    <Overlay :show="showOverlay">
        <b-card title="Import suppliers">
            <div class="d-flex">
                <div class="mr-1 w-75">
                    <b-form-file
                        class="mb-0 pb-0"
                        v-model="file"
                        accept=".xlx, .xlsx, .xlt"
                        placeholder="Choose file"
                        @input="calculateChangeSet"
                    />
                </div>
                <b-button
                    variant="primary w-25"
                    @click="importFile"
                    :disabled="isImportButtonDisabled"
                >
                    Import
                </b-button>
            </div>
        </b-card>
        <b-card v-if="tableData.length > 0" no-body>
            <div class="card-header pb-0">
                <div class="col-md-8 pl-0">
                    <b-card-title>
                        Data control
                    </b-card-title>
                </div>
            </div>
            <HamletTable
                :columns="columns"
                :fetch="renderTable"
                :search="true"
                :td-class-provider="tdClassProvider"
                :default-per-page="30"
                :page-options="[10, 30, 50, 100, 200]"
                ref="table"
            >
                <template #filter-cell-hasIssues>
                    <YesNoSearch
                        style="width: 100px;"
                        @change="(v) => filterRow('hasIssues', v)"/>
                </template>

                <template #cell-hasIssues="data">
                    {{ data.item.hasIssues ? 'Yes' : 'No' }}
                </template>

                <template #filter-cell-organizationName>
                    <FilterInput
                        @search="(v) => filterRow('organizationName', v)"
                    />
                </template>

                <template #cell-organizationName="data">
                    {{ data.item.record.normalizedData.organizationName }}
                    <InstructionInfoSlotButton
                        class="float-right"
                        v-if="data.item.hasIssues && data.item.columnIssues.hasOwnProperty(data.column.key)"
                    >
                        <ul>
                           <li v-for="issue in data.item.columnIssues[data.column.key]">
                               <div>{{ issue.type.name }}, {{ issue.significance }}</div>
                               <div v-if="issue.details instanceof Array" v-for="detail in issue.details">
                                   {{ detail }}
                               </div>
                               <div v-else>{{ issue.details }}</div>
                           </li>
                        </ul>
                    </InstructionInfoSlotButton>
                </template>

                <template #filter-cell-email>
                    <FilterInput
                        @search="(v) => filterRow('email', v)"
                    />
                </template>

                <template #cell-email="data">
                    {{ data.item.record.normalizedData.email }}
                    <InstructionInfoSlotButton
                        class="float-right"
                        v-if="data.item.hasIssues && data.item.columnIssues.hasOwnProperty(data.column.key)"
                    >
                        <ul>
                            <li v-for="issue in data.item.columnIssues[data.column.key]">
                                <div>{{ issue.type.name }}, {{ issue.significance }}</div>
                                <div v-if="issue.details instanceof Array" v-for="detail in issue.details">
                                    {{ detail }}
                                </div>
                                <div v-else>{{ issue.details }}</div>
                            </li>
                        </ul>
                    </InstructionInfoSlotButton>
                </template>

                <template #cell-contact="data">
                    {{ data.item.record.normalizedData.contact }}
                    <InstructionInfoSlotButton
                        class="float-right"
                        v-if="data.item.hasIssues && data.item.columnIssues.hasOwnProperty(data.column.key)"
                    >
                        <ul>
                            <li v-for="issue in data.item.columnIssues[data.column.key]">
                                <div>{{ issue.type.name }}, {{ issue.significance }}</div>
                                <div v-if="Array.isArray(issue.details)">
                                    <div v-for="detail in issue.details">
                                        {{ detail }}
                                    </div>
                                </div>
                                <div v-else>
                                    {{ issue.details }}
                                </div>
                            </li>
                        </ul>
                    </InstructionInfoSlotButton>
                </template>

                <template #filter-cell-country>
                    <FilterInput
                        @search="(v) => filterRow('country', v)"
                    />
                </template>

                <template #cell-country="data">
                    {{ typeof data.item.record.normalizedData.country === 'object' ? data.item.record.normalizedData.country.title : data.item.record.normalizedData.country }}
                    <InstructionInfoSlotButton
                        class="float-right"
                        v-if="data.item.hasIssues && data.item.columnIssues.hasOwnProperty(data.column.key)"
                    >
                        <ul>
                            <li v-for="issue in data.item.columnIssues[data.column.key]">
                                <div>{{ issue.type.name }}, {{ issue.significance }}</div>
                                <div v-if="Array.isArray(issue.details)">
                                    <div v-for="detail in issue.details">
                                        {{ detail }}
                                    </div>
                                </div>
                                <div v-else>
                                    {{ issue.details }}
                                </div>
                            </li>
                        </ul>
                    </InstructionInfoSlotButton>
                </template>

                <template #filter-cell-domain>
                    <FilterInput
                        @search="(v) => filterRow('domain', v)"
                    />
                </template>

                <template #cell-domain="data">
                    {{ data.item.record.normalizedData.domain }}
                    <InstructionInfoSlotButton
                        class="float-right"
                        v-if="data.item.hasIssues && data.item.columnIssues.hasOwnProperty(data.column.key)"
                    >
                        <ul>
                            <li v-for="issue in data.item.columnIssues[data.column.key]">
                                <div>{{ issue.type.name }}, {{ issue.significance }}</div>
                                <div v-if="issue.details instanceof Array" v-for="detail in issue.details">
                                    {{ detail }}
                                </div>
                                <div v-else>{{ issue.details }}</div>
                            </li>
                        </ul>
                    </InstructionInfoSlotButton>
                </template>

                <template #filter-cell-legalIdentity>
                    <FilterInput
                        @search="(v) => filterRow('legalIdentity', v)"
                    />
                </template>

                <template #cell-legalIdentity="data">
                    <LegalIdentity
                        v-if="typeof data.item.record.normalizedData.legalIdentity === 'object'"
                        :view="true"
                        :identity="data.item.record.normalizedData.legalIdentity"
                    />
                    <InstructionInfoSlotButton
                        class="float-right"
                        v-if="data.item.hasIssues && data.item.columnIssues.hasOwnProperty(data.column.key)"
                    >
                        <ul>
                            <li v-for="issue in data.item.columnIssues[data.column.key]">
                                <div>{{ issue.type.name }}, {{ issue.significance }}</div>
                                <div v-if="Array.isArray(issue.details)">
                                    <div v-for="detail in issue.details">
                                        {{ detail }}
                                    </div>
                                </div>
                                <div v-else>
                                    {{ issue.details }}
                                </div>
                            </li>

                        </ul>
                    </InstructionInfoSlotButton>
                </template>
            </HamletTable>
        </b-card>
    </Overlay>
</template>

<script>
import Overlay from "@core/components/common/Overlay";
import {BButton, BCard, BFormGroup, BFormFile, BFormDatepicker, BCardTitle} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {ImportApi} from "@core/api";
import backendResponse from "@core/mixins/ui/backend-response";
import HamletTable from "@core/components/common/HamletTable.vue";
import {Button} from "@core/components/common";
import InstructionInfoSlotButton from "@core/components/common/instructions/InstructionInfoSlotButton.vue";
import YesNoSearch from "@core/components/common/YesNoSearch.vue";
import {FilterInput} from "@core/components/common/filter";
import LegalIdentity from "@core/components/common/LegalIdentity.vue";

export default {
    mixins: [backendResponse],
    components: {
        LegalIdentity,
        FilterInput,
        YesNoSearch,
        InstructionInfoSlotButton,
        Button, HamletTable, BButton, BCard, vSelect, BFormGroup, BFormFile, BFormDatepicker, Overlay, BCardTitle},
    data() {
        return {
            file: null,
            showOverlay: false,
            data: null,
            tableData: [],
            imported: false,

            columns: [
                {
                    key: 'hasIssues',
                    label: 'Issues'
                },
                {
                    key: 'organizationName',
                    label: this.$t('Component(Grid):column-supplier')
                },
                {
                    key: 'legalIdentity',
                    label: 'Legal Identity'
                },
                {
                    key: 'contact',
                    label: this.$t('Component(Grid):column-contact')
                },
                {
                    key: 'email',
                    label: this.$t('Component(Grid):column-email')
                },
                {
                    key: 'domain',
                    label: this.$t('Component(Label):domain')
                },
                {
                    key: 'country',
                    label: 'Country'
                },

            ],
            sheet: null
        };
    },
    methods: {
        filter(filters) {
            let data = this.tableData;

            if (Object.keys(filters).length === 0) {
                return data;
            }

            if (filters.organizationName) {
                filters.organizationName = filters.organizationName.toLowerCase();
                data = data.filter(s => s.record.normalizedData.organizationName.toLowerCase().indexOf(filters.organizationName) >= 0);
            }

            if (filters.email) {
                filters.email = filters.email.toLowerCase();
                data = data.filter(s => s.record.normalizedData.email.toLowerCase().indexOf(filters.email) >= 0);
            }

            if (filters.domain) {
                filters.domain = filters.domain.toLowerCase();
                data = data.filter(s => s.record.normalizedData.domain.toLowerCase().indexOf(filters.domain) >= 0);
            }

            if (filters.country) {
                filters.country = filters.country.toLowerCase();
                data = data.filter(s => s.record.normalizedData.country.toLowerCase().indexOf(filters.country) >= 0);
            }

            if (filters.legalIdentity) {
                const le = filters.legalIdentity.toLowerCase();
                data = data.filter(s => {
                    if (typeof s.record.normalizedData.legalIdentity === 'object') {
                        return s.record.normalizedData.legalIdentity.value.toLowerCase().indexOf(le) >= 0
                    }
                    return false;
                });
            }

            if (false === filters.hasIssues || true === filters.hasIssues) {
                data = data.filter(s => s.hasIssues === filters.hasIssues);
            }

            return data;
        },
        filterRow(property, value) {
            this.$refs.table.filter(property, value);
        },
        renderTable(config, cb) {
            const start = (config.page - 1) * config.perPage;
            const data = this.filter(config.filters);

            cb(
                data.length,
                data.slice(start, start + config.perPage)
            );
        },
        tdClassProvider(column, item) {
            if (Object.keys(item.columnIssues).length > 0) {
                if (item.columnIssues.hasOwnProperty(column.key)) {
                    return 'table-danger';
                }
            }
            return item.hasIssues ? 'table-warning' : '';
        },
        calculateChangeSet() {
            this.doUpload(true);
        },
        importFile() {
            this.doUpload(false);
        },
        doUpload(validation) {
            if (this.file) {
                this.showOverlay = true;
                ImportApi
                    .importSuppliers(validation, this.file)
                    .then(r => {
                        if (validation) {
                            this.prepareTableData(r.data);
                        } else {
                            this.imported = true;
                            this.showDefaultSuccessMessage('Successfully imported');
                        }
                    })
                    .catch(e => {
                        this.handleBackendError(e);
                    })
                    .finally(() => {
                        this.showOverlay = false;
                    })
                ;
            }
        },
        prepareTableData(data) {
            this.data = data;
            this.tableData = data.controlTabs.suppliers.controlRecords;
        }
    },
    computed: {
        isImportButtonDisabled() {
            if (this.imported) {
                return true;
            }
            if (!this.data) {
                return true;
            }

            return true === this.data.hasIssues;
        }
    }
}
</script>
